/**
 * Updates the recently viewed products in localStorage.
 */
export class RecentlyViewed {
  /** @static @constant {string} The key used to store viewed product IDs */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {string} Previous handle key retained for cleanup */
  static #LEGACY_HANDLE_STORAGE_KEY = 'viewedProductHandles';
  /** @static @constant {number} The maximum number of products to store */
  static #MAX_PRODUCTS = 30;

  /**
   * Adds a product to the recently viewed products list.
   * @param {string} productId - The ID of the product to add.
   */
  static addProduct(productId) {
    if (!productId) return;

    let viewedProducts = this.getProducts();

    viewedProducts = viewedProducts.filter((/** @type {string} */ id) => id !== productId);
    viewedProducts.unshift(productId);
    viewedProducts = viewedProducts.slice(0, this.#MAX_PRODUCTS);

    this.#writeList(this.#STORAGE_KEY, viewedProducts);
  }

  static clearProducts() {
    try {
      localStorage.removeItem(this.#STORAGE_KEY);
      localStorage.removeItem(this.#LEGACY_HANDLE_STORAGE_KEY);
    } catch {
      // Storage may be unavailable in privacy-restricted browser contexts.
    }
  }

  /**
   * Retrieves the list of recently viewed products from localStorage.
   * @returns {string[]} The list of viewed products.
   */
  static getProducts() {
    return this.#readList(this.#STORAGE_KEY);
  }

  /**
   * Reads a validated string list from localStorage.
   * @param {string} key - Storage key.
   * @returns {string[]}
   */
  static #readList(key) {
    try {
      const value = JSON.parse(localStorage.getItem(key) || '[]');
      if (!Array.isArray(value)) return [];

      return value.filter((item) => typeof item === 'string' && item.length > 0);
    } catch {
      return [];
    }
  }

  /**
   * Writes a list to localStorage when storage is available.
   * @param {string} key - Storage key.
   * @param {string[]} value - List to store.
   */
  static #writeList(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // Browsing and product links remain usable when storage is unavailable.
    }
  }
}
